import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { rangeOptions } from 'projects/app-default/src/app/common/rangeOptionsFn/rangeOptions';

let uid = 0;

export interface IPageComponentRowAccordionDataColumn {
    // number of columns
    size: number;
    // heading
    heading: string;
    // conatiner for components
    components: NICmsContentComponent.IData[];
}

export interface IPageComponentRowAccordionData {
    // below this size id we make it accordion
    accordionBreakdown: number;
    columns: IPageComponentRowAccordionDataColumn[];
}

export const screenSizeOptions = [
    { key: 3, title: 'Large Desktop' },
    { key: 2, title: 'Small Desktop' },
    { key: 1, title: 'Tablet' },
    { key: 0, title: 'Mobile' }
];

@Component({
    selector: 'page-component-row-accordion',
    templateUrl: './row-accordion.html',
    standalone: true,
    imports: [NgClass, ZippyModule, ICmsClientModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentRowAccordionComponent>({
    id: 'row-accordion',
    name: 'Row breaking to accordion',
    image: '/common/cms/row-accordion-component.png',
    tags: ['row', 'footer'],
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Break to accordion on',
            key: 'accordionBreakdown',
            description: 'Choose display size where row should become accordion',
            options: screenSizeOptions,
            default: 0
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            title: 'Columns',
            key: 'columns',
            itemName: 'column',
            description: 'Columns are displayed in one row till breakdown screen size where they become accordion',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Size',
                    key: 'size',
                    description: `Sizes are relative to other columns, column with size 3 is 3 times wider then column with size 1.
                        Columns fill whole page width.`,
                    options: rangeOptions(1, 12),
                    default: 3
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    title: 'Heading',
                    key: 'heading',
                    required: true,
                    description: 'Column heading on top of column or used as accordion section title'
                },
                <NICmsContentField.IComponentsField>{
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    key: 'components',
                    description: 'Components displayed in column',
                    itemName: 'component',
                    default: []
                }
            ],
            defaultSize: 3
        }
    ]
})
export class PageComponentRowAccordionComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentRowAccordionData;

    private readonly _uid = `rowAccordion${uid++}`;

    get isAccordion(): boolean {
        return this.client.sizeId <= this.component.accordionBreakdown;
    }

    get uid(): string {
        return this._uid;
    }
}
